import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Header from "@/components/Header";

// ── Cinematic background config (regular person avatars only) ────────────────
const ALL_BG_AVATARS = [
  "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png",
  "/36-girl.png", "/38-girl.png", "/27-boy.png",  "/28-boy.png",  "/29-boy.png",
  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png",  "/37-boy.png",
];
const BG_COLS = [
  { dir: "up",   speed: 22, delay: "0s",   opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
  { dir: "down", speed: 29, delay: "2.1s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
  { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
  { dir: "down", speed: 33, delay: "1.1s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
  { dir: "up",   speed: 25, delay: "0s",   opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
  { dir: "down", speed: 20, delay: "1.6s", opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
  { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
  { dir: "down", speed: 18, delay: "2.6s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
  { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
  { dir: "down", speed: 23, delay: "1.3s", opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
];
const SPARKS = [
  { left: "8%",  delay: "0s",   dx: "12px",  dur: "2.4s" }, { left: "22%", delay: "1.1s", dx: "-9px",  dur: "2.9s" },
  { left: "38%", delay: "0.4s", dx: "16px",  dur: "2.2s" }, { left: "52%", delay: "1.8s", dx: "-11px", dur: "3.1s" },
  { left: "63%", delay: "0.7s", dx: "8px",   dur: "2.6s" }, { left: "76%", delay: "1.4s", dx: "-14px", dur: "2.0s" },
  { left: "88%", delay: "0.2s", dx: "10px",  dur: "2.7s" }, { left: "45%", delay: "2.3s", dx: "-7px",  dur: "2.3s" },
];
function getBgCol(ci) {
  const imgs = [];
  for (let i = 0; i < 4; i++) imgs.push(ALL_BG_AVATARS[(ci * 3 + i) % ALL_BG_AVATARS.length]);
  return [...imgs, ...imgs];
}

export default function MockLogin() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const returnTo  = new URLSearchParams(location.search).get("returnTo");
  const afterApproved = "/mock/positions";

  const [step,   setStep]   = useState("email");
  const [email,  setEmail]  = useState("");
  const [name,   setName]   = useState("");
  const [otp,    setOtp]    = useState("");
  const [busy,   setBusy]   = useState(false);
  const [devOtp, setDevOtp] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("mock_token");
    if (token) {
      api.mockMe()
        .then((d) => navigate(d.approved ? afterApproved : "/mock/pending", { replace: true }))
        .catch(() => {
          // Stale / invalid token — clear it so the login form works cleanly
          localStorage.removeItem("mock_token");
          localStorage.removeItem("mock_email");
          localStorage.removeItem("mock_name");
        });
    }
  }, []);

  async function handleSendOtp(e) {
    e.preventDefault();
    if (!email.endsWith("@kibalabs.in")) {
      toast.error("Only @kibalabs.in email addresses are allowed");
      return;
    }
    setBusy(true);
    try {
      const res = await api.mockSendOtp(email.trim().toLowerCase(), name.trim());
      if (res.dev_otp) setDevOtp(res.dev_otp);
      setStep("otp");
      toast.success("OTP sent to " + email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.mockVerifyOtp(email.trim().toLowerCase(), otp.trim());
      localStorage.setItem("mock_token", res.token);
      localStorage.setItem("mock_email", email.trim().toLowerCase());
      localStorage.setItem("mock_name", res.name || name);
      navigate(res.approved ? afterApproved : "/mock/pending", { replace: true });
    } catch (err) {
      if (!err?.response) {
        toast.error("Connection error — check your network and try again");
      } else {
        toast.error(err.response.data?.detail || "Invalid OTP — please try again");
      }
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* ── Cinematic keyframes ── */}
      <style>{`
        @keyframes ml-up    { 0%{transform:translateY(0)}     100%{transform:translateY(-50%)} }
        @keyframes ml-down  { 0%{transform:translateY(-50%)}  100%{transform:translateY(0)} }
        @keyframes ml-ambi  { 0%,100%{opacity:0.10} 50%{opacity:0.26} }
        @keyframes ml-ambi2 { 0%,100%{opacity:0.06} 50%{opacity:0.18} }
        @keyframes ml-blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
      `}</style>

      {/* ── Wall of Fighters background ── */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
        {BG_COLS.map((col, ci) => (
          <div key={ci} style={{
            position: "absolute", left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
            overflow: "hidden", opacity: col.opacity,
            filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
          }}>
            <div style={{
              display: "flex", flexDirection: "column", gap: 6,
              animation: `${col.dir === "up" ? "ml-up" : "ml-down"} ${col.speed}s linear ${col.delay} infinite`,
            }}>
              {getBgCol(ci).map((src, ii) => (
                <img key={ii} src={src} alt="" style={{
                  width: "100%", height: "27vh", objectFit: "cover", objectPosition: "top center",
                  borderRadius: 8, flexShrink: 0,
                  filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.2)`,
                }} />
              ))}
            </div>
          </div>
        ))}

        {/* Side fades */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(6,10,31,0.88) 0%, transparent 16%, transparent 84%, rgba(6,10,31,0.88) 100%)" }} />
        {/* Top/bottom fades */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(6,10,31,0.90) 0%, transparent 14%, transparent 86%, rgba(6,10,31,0.90) 100%)" }} />
        {/* Centre dark stage */}
        <div style={{ position: "absolute", inset: 0, zIndex: 2, background: "radial-gradient(ellipse 48% 80% at 50% 48%, rgba(6,10,31,0.92) 0%, rgba(6,10,31,0.70) 48%, transparent 100%)" }} />
        {/* Red arena glows */}
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 800, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.14) 0%, transparent 65%)", animation: "ml-ambi 3.5s ease-in-out infinite", zIndex: 3 }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "50%", transform: "translateX(-50%)", width: 700, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.10) 0%, transparent 65%)", animation: "ml-ambi2 4.5s ease-in-out 1s infinite", zIndex: 3 }} />
        {/* Sparks */}
        {SPARKS.map((s, i) => (
          <div key={i} style={{ position: "absolute", left: s.left, bottom: "5%", width: 5, height: 5, borderRadius: "50%", background: "var(--kl-red)", boxShadow: "0 0 10px rgba(230,57,70,1), 0 0 20px rgba(230,57,70,0.6)", zIndex: 4 }} />
        ))}
        {/* Cyber grid */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.035, zIndex: 2, backgroundImage: "linear-gradient(rgba(230,57,70,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(230,57,70,0.9) 1px, transparent 1px)", backgroundSize: "55px 55px" }} />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-5 md:px-10 py-8">
        <div className="w-full max-w-2xl">

          {/* Page label */}
          <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-3">
            <span className="kl-accent-line mr-3" />Proctorly AI · Mock Interviews
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tight mb-8 md:mb-10">
            {step === "email"
              ? <>Sign <span className="text-[color:var(--kl-red)]">in.</span></>
              : <>Your <span className="text-[color:var(--kl-red)]">code.</span></>
            }
          </h1>

          {/* Form card */}
          <div className="rounded-2xl border border-white/15 p-8 md:p-10 space-y-7"
            style={{ background: "rgba(6,10,31,0.80)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>

            {step === "email" ? (
              <form onSubmit={handleSendOtp} className="space-y-7">
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>
                    Full Name <span style={{ color: "#e63946" }}>*</span>
                  </label>
                  <input
                    className="input-dark"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Your Full Name"
                    autoComplete="name"
                    style={{ fontSize: 17, padding: "14px 16px" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>
                    Kiba Labs Email <span style={{ color: "#e63946" }}>*</span>
                  </label>
                  <input
                    className="input-dark"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="yourname@kibalabs.in"
                    autoComplete="email"
                    style={{ fontSize: 17, padding: "14px 16px" }}
                  />
                  <p className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30 mt-2">
                    Only @kibalabs.in addresses are accepted
                  </p>
                </div>
                <button type="submit" disabled={busy} className="btn-submit w-full justify-center disabled:opacity-40"
                  style={{ fontSize: 16, padding: "16px 24px" }}>
                  {busy
                    ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Sending…</>
                    : <>Send OTP <ArrowUpRight className="w-5 h-5" /></>
                  }
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-7">
                <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)" }}>
                  OTP sent to <span style={{ color: "#fff", fontWeight: 600 }}>{email}</span>
                </p>

                {devOtp && (
                  <div className="rounded-xl border p-5 text-center"
                    style={{ background: "rgba(230,57,70,0.08)", borderColor: "rgba(230,57,70,0.4)" }}>
                    <div className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)] mb-3">
                      Your OTP
                    </div>
                    <div className="font-display text-5xl tracking-[0.5em] text-white">{devOtp}</div>
                    <button
                      type="button"
                      onClick={() => setOtp(devOtp)}
                      className="btn-ghost-dark text-sm w-full justify-center mt-4"
                    >
                      ↓ Auto-fill OTP
                    </button>
                  </div>
                )}

                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>
                    Enter 6-digit OTP <span style={{ color: "#e63946" }}>*</span>
                  </label>
                  <input
                    className="input-dark"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    placeholder="000000"
                    maxLength={6}
                    style={{ textAlign: "center", letterSpacing: 14, fontSize: 28, fontWeight: 700, padding: "16px 16px" }}
                  />
                </div>

                <button type="submit" disabled={busy || otp.length !== 6} className="btn-submit w-full justify-center disabled:opacity-40"
                  style={{ fontSize: 16, padding: "16px 24px" }}>
                  {busy
                    ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Verifying…</>
                    : <>Verify &amp; Continue <ArrowUpRight className="w-5 h-5" /></>
                  }
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setOtp(""); setDevOtp(""); }}
                  className="btn-ghost-dark w-full justify-center"
                  style={{ fontSize: 14 }}
                >
                  ← Change email
                </button>
              </form>
            )}
          </div>

          <p className="text-white/25 font-mono-kl text-[10px] uppercase tracking-widest text-center mt-6">
            Access requires admin approval for first-time users · Sessions are recorded
          </p>
          <div className="mt-4 text-center text-white/20 font-mono-kl text-[10px] uppercase tracking-widest">
            Proctorly AI · Powered by Kiba Labs
          </div>
        </div>
      </div>
    </div>
  );
}
